import { lazy, Suspense, useEffect, useRef } from "react";
import gsap from "gsap";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useSmoothScroll } from "./hooks/useSmoothScroll";
import {
  createRouteSchema,
  getCanonicalUrl,
  getRouteSeo,
  SITE_URL,
} from "./seo";

const HomePage = lazy(() => import("./pages/HomePage").then((module) => ({ default: module.HomePage })));
const NordPage = lazy(() => import("./pages/NordPage").then((module) => ({ default: module.NordPage })));
const SillagePage = lazy(() => import("./pages/SillagePage").then((module) => ({ default: module.SillagePage })));
const FieldPage = lazy(() => import("./pages/FieldPage").then((module) => ({ default: module.FieldPage })));
const GovoriPage = lazy(() => import("./pages/GovoriPage").then((module) => ({ default: module.GovoriPage })));
const NullFieldPage = lazy(() => import("./pages/NullFieldPage").then((module) => ({ default: module.NullFieldPage })));
const SloiStorePage = lazy(() => import("./pages/SloiStorePage").then((module) => ({ default: module.SloiStorePage })));

function RouteEffects() {
  const location = useLocation();
  const previousTrackedUrl = useRef<string | null>(null);

  useEffect(() => {
    const seo = getRouteSeo(location.pathname);
    const canonical = getCanonicalUrl(location.pathname);
    const absoluteImage = `${SITE_URL}${seo.image}`;

    const setMeta = (selector: string, content: string) => {
      document.querySelector<HTMLMetaElement>(selector)?.setAttribute("content", content);
    };

    document.title = seo.title;
    setMeta('meta[name="description"]', seo.description);
    setMeta('meta[property="og:title"]', seo.title);
    setMeta('meta[property="og:description"]', seo.description);
    setMeta('meta[property="og:type"]', seo.pageType);
    setMeta('meta[property="og:url"]', canonical);
    setMeta('meta[property="og:image"]', absoluteImage);
    setMeta('meta[property="og:image:alt"]', seo.imageAlt);
    setMeta('meta[name="twitter:title"]', seo.title);
    setMeta('meta[name="twitter:description"]', seo.description);
    setMeta('meta[name="twitter:image"]', absoluteImage);
    setMeta('meta[name="twitter:image:alt"]', seo.imageAlt);
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute("href", canonical);

    const schema = document.querySelector<HTMLScriptElement>("#route-schema");
    if (schema) schema.textContent = JSON.stringify(createRouteSchema(location.pathname, seo));

    const currentTrackedUrl = window.location.href;
    if (previousTrackedUrl.current && previousTrackedUrl.current !== currentTrackedUrl) {
      window.ym?.(110820752, "hit", currentTrackedUrl, {
        title: seo.title,
        referer: previousTrackedUrl.current,
      });
    }
    previousTrackedUrl.current = currentTrackedUrl;

    gsap.fromTo(
      ".route-curtain",
      { scaleY: 1, transformOrigin: "top" },
      { scaleY: 0, duration: 0.9, ease: "expo.inOut" },
    );
  }, [location.pathname, location.search]);

  return null;
}

function PageProgress() {
  const location = useLocation();
  const bar = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!bar.current) return;

    const setScale = gsap.quickSetter(bar.current, "scaleX");
    let frame = 0;

    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScale(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    document.fonts?.ready.then(requestUpdate);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, [location.pathname]);

  const route = location.pathname === "/" ? "home" : location.pathname.slice(1);

  return (
    <div className="page-progress" data-route={route} aria-hidden="true">
      <span ref={bar} />
    </div>
  );
}

export default function App() {
  const location = useLocation();
  useSmoothScroll(location.key);

  return (
    <>
      <div className="route-curtain" aria-hidden="true" />
      <PageProgress />
      <a className="skip-link" href="#main">
        Перейти к содержанию
      </a>
      <RouteEffects />
      <Suspense
        fallback={(
          <div className="route-loader" role="status" aria-live="polite">
            <span aria-hidden="true"><i /><i /><i /></span>
            <p>Собираю следующую сцену</p>
          </div>
        )}
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/nord-79" element={<NordPage />} />
          <Route path="/sillage" element={<SillagePage />} />
          <Route path="/field-os" element={<FieldPage />} />
          <Route path="/govori" element={<GovoriPage />} />
          <Route path="/null-field" element={<NullFieldPage />} />
          <Route path="/sloi-store/*" element={<SloiStorePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
